import { useState, type SyntheticEvent } from "react";

import { Box } from "ui/box";
import { Tab, Tabs } from "ui/tabs";
import { Typography } from "ui/typography";
import { PreviewControls } from "containers/admin/clients/client/theme-configuration-page/theme-preview/preview-controls";
import { PreviewFrame } from "containers/admin/clients/client/theme-configuration-page/theme-preview/preview-frame";

import { useCurrentClient } from "hooks/api/use-clients";
import { useEmailTypes } from "hooks/api/use-email-types";
import { useThemeQuery } from "hooks/api/use-theme";

import { getErrorMessage } from "utilities";

import { buildEmailPreviewDocument } from "containers/admin/clients/client/theme-configuration-page/theme-preview/templates/email-preview-document";
import { getDefaultSender } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.helpers";

import { PAGE_SX } from "containers/admin/clients/client/theme-configuration-page/theme-configuration.styles";
import {
  ERROR_SX,
  PREVIEW_SX,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.styles";
import {
  PREVIEW_TABS,
  THEME_LOAD_ERROR_MESSAGE,
  type PreviewTab,
  type ScreenSize,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

export const ThemePreview = () => {
  const client = useCurrentClient();
  const { data: theme, isLoading: isThemeLoading, error: themeError } = useThemeQuery(client.id);
  // A failed request falls back to "Not defined", as the legacy preview did; only the wait matters here.
  const { data: emailTypes, isLoading: areEmailTypesLoading } = useEmailTypes(client.id);

  const [activeTab, setActiveTab] = useState<PreviewTab>("email");
  const [screenSize, setScreenSize] = useState<ScreenSize>("small");
  const [isBaseFontApplied, setIsBaseFontApplied] = useState(false);

  const srcDoc =
    isThemeLoading || areEmailTypesLoading
      ? null
      : buildEmailPreviewDocument({
          theme,
          sender: getDefaultSender(emailTypes),
          clientName: client.name,
          isBaseFontApplied,
        });

  const handleTabChange = (_event: SyntheticEvent, tab: PreviewTab) => setActiveTab(tab);

  return (
    <Box sx={PAGE_SX}>
      <Tabs appearance="sub-nav" value={activeTab} onChange={handleTabChange} aria-label="Preview type">
        {PREVIEW_TABS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      <Box sx={PREVIEW_SX}>
        <PreviewControls
          screenSize={screenSize}
          onScreenSizeChange={setScreenSize}
          isBaseFontApplied={isBaseFontApplied}
          onBaseFontChange={setIsBaseFontApplied}
        />
        {themeError ? (
          <Typography variant="body1" component="p" role="alert" sx={ERROR_SX}>
            {getErrorMessage(themeError) || THEME_LOAD_ERROR_MESSAGE}
          </Typography>
        ) : (
          <PreviewFrame title="Email theme preview" srcDoc={srcDoc} screenSize={screenSize} />
        )}
      </Box>
    </Box>
  );
};
